import { useMemo, forwardRef, HTMLProps, ReactNode } from "react";
import Link from "next/link";
import { cn } from "@/utils/helpers";
import styles from "./Button.module.css";

export interface ButtonProps extends HTMLProps<HTMLButtonElement & HTMLAnchorElement> {
	href?: string;
	variant?: "default";
	color?: "primary" | "secondary" | "tertiary" | "highlight";
	filled?: boolean;
	outlined?: boolean;
	selected?: boolean;
	pressed?: boolean;
	square?: boolean;
	// wide?: boolean;
	// large?: boolean;
	// arrow?: boolean;
	tag?: string;
	external?: boolean;
	label?: string;
	description?: string;
	before?: ReactNode;
	after?: ReactNode;
}

export default forwardRef(function Button({
	href,
	variant = "default",
	color = "primary",
	filled,
	outlined,
	selected,
	pressed,
	square,
	// wide,
	// large,
	// arrow,
	tag = "button",
	external,
	label,
	description,
	before,
	after,
	onClick,
	className,
	children,
	...props
} : ButtonProps, ref) {
	const Tag = href
		? external
			? "a"
			: Link
		: tag;
	const buttonProps = useMemo(() => {
		let elemProps;

		if(Tag === "a" || typeof Tag === "function") {
			elemProps = {
				...props,
				href: href
			} as HTMLProps<HTMLAnchorElement>;
			elemProps.href = href;
			elemProps.target = external ? "_blank" : "_self";
		} else if(Tag === "button") {
			elemProps = { ...props } as HTMLProps<HTMLButtonElement>;
		} else {
			elemProps = { ...props } as HTMLProps<HTMLElement>;
		}

		if(onClick) {
			elemProps.onClick = onClick;
		}
		return elemProps;
	}, [href, external, props, Tag, onClick]) as any;

	return (
		<Tag
			ref={ref}
			className={cn(
				styles.Button,
				styles[`Button_${variant}`],
				color ? styles[`Button_${color}`] : null,
				filled ? styles.Button_filled : null,
				outlined ? styles.Button_outlined : null,
				square ? styles.Button_square : null,
				// wide ? styles.Button_wide : null,
				// large ? styles.Button_large : null,
				// arrow ? styles.Button_arrow : null,
				before ? styles.Button_before : null,
				after ? styles.Button_after : null,
				// iconReverse ? styles.Button_iconReverse : null,
				external ? styles.Button_external : null,
				className
			)}
			aria-label={label}
			aria-description={description}
			aria-selected={selected}
			aria-pressed={pressed}
			tabIndex={0}
			{...buttonProps}
			{...props}
		>
			{before ?
				<span
					className={cn(
						styles.ButtonIcon,
						styles.ButtonIcon_before
					)}
				>
					{before}
				</span>
			: null}
			{children && <span>{children}</span>}
			{after ?
				<span
					className={cn(
						styles.ButtonIcon,
						styles.ButtonIcon_after
					)}
				>
					{after}
				</span>
			: null}
		</Tag>
	)
});